import { useEffect, useMemo, useState } from "react";
import { Camera, Loader2, Phone, Plane, Power, Save, Shield, User, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FormSelect } from "@/components/campos-formulario/novos/FormSelect";

import { pessoaService } from "@/services/api/PessoaService";
import { Role, roleService } from "@/services/api/RoleService";
import { squadService } from "@/services/api/squad.service";
import { useAusenciaPessoa, useGestaoAusencia } from "@/hooks/pessoas/use-ausencia-pessoa";

import { SquadDTO } from "@/types/squad.type";
import { PessoaDTO } from "@/types/pessoa";
import { RoleLabelsMap } from "@workflow/types";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { formatPhoneNumber } from "@/utils/phone-format";
import { SearchableSelectionList } from "./SearchableSelectionList";

/**
 * Cabeçalho de seção padronizado: chip de ícone + título em maiúsculas, com
 * badge opcional alinhado à direita. Mantém todas as seções visualmente
 * consistentes (e enxuga a marcação repetida).
 */
const SectionHeader = ({
  icon,
  title,
  badge,
}: {
  icon: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
}) => (
  <div className="flex items-center gap-2.5 border-b border-border pb-2">
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
      {icon}
    </div>
    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {title}
    </h3>
    {badge && <div className="ml-auto">{badge}</div>}
  </div>
);

/** Badge "X de Y" usado nos contadores de seleção. */
const CountBadge = ({ selected, total }: { selected: number; total: number }) => (
  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
    {selected} de {total}
  </span>
);

interface PessoaDialogEditProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: Role[];
  squads: SquadDTO[];
  /** Pessoas disponíveis como substituto (o pai já carrega a lista). */
  pessoas: PessoaDTO[];
  editingUserRoleIds: number[];
  editingUserSquadIds: number[];
  onRoleToggle: (roleId: number) => void;
  onSquadToggle: (squadId: number) => void;
  onSave: () => void;
  onCancel: () => void;
  editingUser: PessoaDTO | null;
}

export function PessoaDialogEdit({
  open,
  onOpenChange,
  roles,
  squads,
  pessoas,
  editingUserRoleIds,
  editingUserSquadIds,
  onRoleToggle,
  onSquadToggle,
  onSave,
  onCancel,
  editingUser,
}: PessoaDialogEditProps) {
  const isMobile = useIsMobile();

  // States Locais
  const [telefone, setTelefone] = useState("");
  const [foto, setFoto] = useState("");
  const [ativo, setAtivo] = useState(false);

  // Ausência / Férias
  const [ferias, setFerias] = useState(false);
  const [substitutoId, setSubstitutoId] = useState<number | null>(null);
  const [initialSubstitutoId, setInitialSubstitutoId] = useState<number | null>(null);

  // Unifica o controle de loading
  const [isProcessing, setIsProcessing] = useState(false);

  // Substitutos possíveis: reaproveita a lista que o pai já carregou (sem refetch).
  const substitutos = useMemo(
    () => pessoas.filter((p) => p.id !== editingUser?.id),
    [pessoas, editingUser?.id],
  );

  // Ausência ativa da pessoa (React Query) — usada para pré-preencher o substituto.
  const { data: ausenciaAtual } = useAusenciaPessoa(
    editingUser?.id,
    open && !!editingUser?.ausente,
  );
  const { marcar, remover } = useGestaoAusencia(editingUser?.id);

  const isLoading = isProcessing || marcar.isPending || remover.isPending;

  // Sincronização dos campos do formulário ao abrir
  useEffect(() => {
    if (open && editingUser) {
      setTelefone(editingUser.telefone || "");
      setFoto(editingUser.foto || "");
      setAtivo(editingUser.ativo ?? false);
      setFerias(editingUser.ausente ?? false);
    }
  }, [editingUser, open]);

  // Pré-preenche o substituto a partir da ausência ativa (cacheada pelo RQ)
  useEffect(() => {
    const subId = ausenciaAtual?.substituto?.id ?? null;
    setSubstitutoId(subId);
    setInitialSubstitutoId(subId);
  }, [ausenciaAtual]);

  // Guard Clause
  if (!editingUser) return null;

  // Helper
  const areArraysEqual = (arr1: number[], arr2: number[]) => {
    return JSON.stringify([...arr1].sort()) === JSON.stringify([...arr2].sort());
  };

  // Orchestrator de Salvamento
  const handleSave = async () => {
    const currentRoleIds = editingUser.roles?.map((role) => role.id) || [];
    const currentSquadIds =
      editingUser.squads?.map((squad) => squad.id).filter((id): id is number => id !== undefined) ||
      [];

    const rolesChanged = !areArraysEqual(currentRoleIds, editingUserRoleIds);
    const squadsChanged = !areArraysEqual(currentSquadIds, editingUserSquadIds);

    const pessoaDataChanged = telefone !== editingUser.telefone || foto !== editingUser.foto;
    const statusChanged = editingUser.ativo !== ativo;

    const wasAusente = editingUser.ausente ?? false;
    const feriasChanged = ferias !== wasAusente;
    const substitutoChanged = ferias && substitutoId !== initialSubstitutoId;
    const ausenciaChanged = feriasChanged || substitutoChanged;

    if (
      !pessoaDataChanged &&
      !rolesChanged &&
      !statusChanged &&
      !squadsChanged &&
      !ausenciaChanged
    ) {
      onCancel();
      return;
    }

    setIsProcessing(true);

    try {
      const promises: Promise<any>[] = [];

      // 1. Atualiza Dados Básicos
      if (pessoaDataChanged) {
        promises.push(
          pessoaService.updatePessoa(editingUser.id, {
            telefone: telefone || "",
            foto: foto || undefined,
          }),
        );
      }

      // 2. Atualiza Roles
      if (rolesChanged) {
        promises.push(
          roleService.assignRolesToUser({
            userId: editingUser.id,
            roleIds: editingUserRoleIds,
          }),
        );
      }

      // 3. Atualiza Squads
      if (squadsChanged) {
        const squadsToAdd = editingUserSquadIds.filter((id) => !currentSquadIds.includes(id));
        const squadsToRemove = currentSquadIds.filter((id) => !editingUserSquadIds.includes(id));

        squadsToAdd.forEach((squadId) =>
          promises.push(squadService.vincularMembro(squadId, editingUser.id)),
        );
        squadsToRemove.forEach((squadId) =>
          promises.push(squadService.removerMembro(squadId, editingUser.id)),
        );
      }

      // 4. Atualiza Status
      if (statusChanged) {
        if (ativo && !editingUser.ativo) {
          promises.push(pessoaService.ativarPessoa(editingUser.id));
        } else if (!ativo && editingUser.ativo) {
          promises.push(pessoaService.deletePessoa(editingUser.id));
        }
      }

      // 5. Atualiza Ausência / Férias (mutations React Query — invalidam o cache)
      if (ausenciaChanged) {
        if (ferias) {
          // Liga ou atualiza o substituto (o endpoint cria ou atualiza a ausência ativa)
          promises.push(marcar.mutateAsync({ substitutoId: substitutoId ?? undefined }));
        } else {
          // Desliga o modo férias
          promises.push(remover.mutateAsync());
        }
      }

      await Promise.all(promises);
      onSave();
      toast.success("Usuário atualizado com sucesso");
    } catch (e) {
      console.error(e);
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Sub-componentes ---

  const renderInputGroup = (
    id: string,
    label: string,
    value: string,
    setValue: (v: string) => void,
    icon: React.ReactNode,
    placeholder: string,
  ) => (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
      </Label>
      <div className="group relative">
        <div className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary">
          {icon}
        </div>
        <Input
          id={id}
          value={value}
          onChange={(e) => setValue(e.target.value || "")}
          placeholder={placeholder}
          className="border-input bg-background pl-10 focus-visible:ring-ring"
          disabled={isLoading}
        />
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex flex-col gap-0 overflow-hidden border-border bg-card p-0 text-card-foreground",
          isMobile
            ? "h-[100dvh] w-full max-w-none rounded-none border-0"
            : "max-h-[85vh] sm:max-w-5xl",
        )}
      >
        {/* Header */}
        <DialogHeader className="relative shrink-0 overflow-hidden border-b border-border bg-gradient-to-br from-primary/10 via-card to-card px-6 py-6">
          {/* Brilho decorativo */}
          <div className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full bg-primary/20 opacity-60 blur-3xl" />

          <div className="relative flex items-center gap-4">
            <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary/70 shadow-lg shadow-primary/25 ring-1 ring-inset ring-white/10">
              {foto ? (
                <img src={foto} alt={editingUser.nome} className="h-full w-full object-cover" />
              ) : (
                <User className="h-7 w-7 text-primary-foreground" />
              )}
            </div>
            <div className="flex flex-col gap-0.5 overflow-hidden">
              <DialogTitle
                className="truncate text-xl leading-tight text-foreground"
                title={editingUser.nome}
              >
                {editingUser.nome}
              </DialogTitle>
              <span className="truncate text-sm text-muted-foreground" title={editingUser.email}>
                {editingUser.email}
              </span>
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="custom-scrollbar flex-1 overflow-y-auto bg-background/50 px-6 py-6">
          <div className="grid gap-8">
            {/* Seção 1: Dados Pessoais (largura total, campos lado a lado) */}
            <div className="space-y-4">
              <SectionHeader icon={<User className="h-4 w-4" />} title="Dados Pessoais" />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {renderInputGroup(
                  "edit-telefone",
                  "Telefone",
                  telefone,
                  (v) => setTelefone(formatPhoneNumber(v)),
                  <Phone className="h-4 w-4" />,
                  "+55 (00) 00000-0000",
                )}
                {renderInputGroup(
                  "edit-foto",
                  "Foto de Perfil (URL)",
                  foto,
                  setFoto,
                  <Camera className="h-4 w-4" />,
                  "https://...",
                )}
              </div>
            </div>

            {/* Seção 2: Acesso & Disponibilidade (dois cards de toggle, lado a lado) */}
            <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
              {/* Controle de Acesso */}
              <div className="space-y-4">
                <SectionHeader icon={<Power className="h-4 w-4" />} title="Controle de Acesso" />
                <div
                  className={cn(
                    "rounded-xl border p-5 transition-all duration-300",
                    !ativo
                      ? "border-destructive/30 bg-destructive/10"
                      : "border-border bg-background",
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="status-switch"
                        className={cn(
                          "cursor-pointer text-base font-semibold",
                          !ativo && "text-destructive",
                        )}
                      >
                        {ativo ? "Conta Ativa" : "Conta Bloqueada"}
                      </Label>
                      <p className="text-sm leading-snug text-muted-foreground">
                        {ativo
                          ? "O usuário tem permissão para acessar o sistema."
                          : "O usuário está inativado e sem acesso."}
                      </p>
                    </div>
                    <Switch
                      id="status-switch"
                      checked={ativo}
                      onCheckedChange={setAtivo}
                      disabled={isLoading}
                      className={cn("mt-1", !ativo && "data-[state=unchecked]:bg-destructive")}
                    />
                  </div>
                </div>
              </div>

              {/* Disponibilidade / Férias */}
              <div className="space-y-4">
                <SectionHeader icon={<Plane className="h-4 w-4" />} title="Disponibilidade" />
                <div
                  className={cn(
                    "space-y-4 rounded-xl border p-5 transition-all duration-300",
                    ferias ? "border-orange-500/30 bg-orange-500/5" : "border-border bg-background",
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="ferias-switch"
                        className={cn(
                          "cursor-pointer text-base font-semibold",
                          ferias && "text-orange-600",
                        )}
                      >
                        {ferias ? "Em Férias / Ausente" : "Disponível"}
                      </Label>
                      <p className="text-sm leading-snug text-muted-foreground">
                        {ferias
                          ? "Notificações são redirecionadas ao substituto enquanto a pessoa estiver ausente."
                          : "Marque para colocar a pessoa em férias/ausência."}
                      </p>
                    </div>
                    <Switch
                      id="ferias-switch"
                      checked={ferias}
                      onCheckedChange={setFerias}
                      disabled={isLoading}
                      className="mt-1 data-[state=checked]:bg-orange-500"
                    />
                  </div>

                  {ferias && (
                    <FormSelect
                      label="Substituto (opcional)"
                      value={substitutoId?.toString() || ""}
                      onChange={(v) => setSubstitutoId(v ? Number(v) : null)}
                      placeholder="Selecione um substituto..."
                      options={substitutos.map((p) => ({ label: p.nome, value: p.id.toString() }))}
                      disabled={isLoading}
                      allowClear
                      helperText="A pessoa escolhida receberá os alertas e poderá agir no lugar durante a ausência."
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Seção 2: Permissões e Squads */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Funções */}
              <div className="space-y-3">
                <SectionHeader
                  icon={<Shield className="h-4 w-4" />}
                  title="Cargos Atribuídos"
                  badge={<CountBadge selected={editingUserRoleIds.length} total={roles.length} />}
                />
                <SearchableSelectionList
                  items={roles}
                  selectedIds={editingUserRoleIds}
                  onToggle={onRoleToggle}
                  getId={(role) => role.id}
                  getLabel={(role) => RoleLabelsMap[role.nome] || role.nome}
                  emptyMessage="Nenhuma função disponível."
                  searchPlaceholder="Buscar função..."
                  disabled={isLoading}
                />
              </div>

              {/* Squads */}
              <div className="space-y-3">
                <SectionHeader
                  icon={<Users className="h-4 w-4" />}
                  title="Vínculo de Squads"
                  badge={<CountBadge selected={editingUserSquadIds.length} total={squads.length} />}
                />
                <SearchableSelectionList
                  items={squads}
                  selectedIds={editingUserSquadIds}
                  onToggle={onSquadToggle}
                  getId={(squad) => squad.id}
                  getLabel={(squad) => squad.nome}
                  emptyMessage="Nenhum squad disponível."
                  searchPlaceholder="Buscar squad..."
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="shrink-0 flex-row justify-end gap-3 border-t border-border bg-muted/30 px-6 py-4 sm:gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 border-border hover:bg-accent hover:text-accent-foreground sm:flex-none"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="min-w-[150px] flex-1 gap-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-md shadow-primary/20 transition-shadow hover:shadow-lg hover:shadow-primary/30 sm:flex-none"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Salvar Alterações
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
